import React from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { DetailSection, DetailValue, EmptyValue } from "../detailView";
import { countryName } from "../../../utils/countries";

type OpeningHourLike = {
  id?: string;
  week_day: string;
  from_hour?: string;
  to_hour?: string;
};

interface Address {
  street?: string;
  city?: string;
  zip_code?: string;
  country?: string;
  phone_number?: string;
  email?: string;
  email_counter_reminders?: string;
  notes?: string;
  opening_hours?: OpeningHourLike[];
}

interface AddressDisplayProps {
  address: Address | null | undefined;
  /** Defaults to "Address & Opening Hours". */
  title?: string;
  icon?: React.ReactNode;
  /** Add the address notes and opening hours as rows. */
  withDetails?: boolean;
  /** Add the phone number and emails kept on the address. */
  withContact?: boolean;
  /** Let the header fold the address away. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Shown instead of the rows when there is no address at all. */
  emptyContent?: React.ReactNode;
}

const buildGoogleMapsUrl = (addr: Address): string => {
  const addressParts = [addr.street, addr.city, addr.zip_code, addr.country]
    .filter(Boolean)
    .join(", ");

  if (!addressParts) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
};

// Drop the seconds the API sends, e.g. "08:00:00" -> "08:00".
const formatTime = (time?: string) => (time ? time.split(":").slice(0, 2).join(":") : "");

/** An address's weekly opening hours, one line per entry. */
export const OpeningHoursValue = ({ openingHours }: { openingHours?: OpeningHourLike[] | null }) => {
  const { t } = useTranslation();
  const dayNames: Record<string, string> = {
    "1": t("Monday"),
    "2": t("Tuesday"),
    "3": t("Wednesday"),
    "4": t("Thursday"),
    "5": t("Friday"),
    "6": t("Saturday"),
    "7": t("Sunday"),
  };

  if (!Array.isArray(openingHours) || openingHours.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
        {t("No opening hours")}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {openingHours.map((oh, idx) => (
        <DetailValue key={oh.id || idx}>
          {typeof oh === "string"
            ? oh
            : `${dayNames[oh.week_day] || oh.week_day}: ${formatTime(oh.from_hour)} - ${formatTime(oh.to_hour)}`}
        </DetailValue>
      ))}
    </Box>
  );
};

/** A caption-headed block inside {@link AddressColumns}. */
const ColumnBlock = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
    <Typography variant="body2" component="div" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ContactLine = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
    <Box sx={{ display: "flex", color: "text.secondary", "& svg": { fontSize: 18 } }}>{icon}</Box>
    <DetailValue>{children}</DetailValue>
  </Box>
);

/**
 * The body of {@link AddressDisplay}: the postal address, contact and notes on
 * the left, opening hours on the right - only the filled-in fields, as plain text.
 */
const AddressColumns = ({
  address,
  withDetails,
  withContact,
}: {
  address: Address | null | undefined;
  withDetails: boolean;
  withContact: boolean;
}) => {
  const { t } = useTranslation();
  const cityLine = [address?.zip_code, address?.city].filter(Boolean).join(" ");
  const addressLines = [address?.street, cityLine, countryName(address?.country)].filter(Boolean);
  const hasContact =
    withContact && (address?.phone_number || address?.email || address?.email_counter_reminders);

  return (
    <Box
      sx={{
        display: "grid",
        // Keyed on the card's width, so it drops to one column in narrow spots
        // such as a task row or the preview modal.
        gridTemplateColumns: withDetails ? "repeat(auto-fit, minmax(min(240px, 100%), 1fr))" : "1fr",
        columnGap: 3,
        rowGap: 2,
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
        <ColumnBlock label={t("Address")}>
          {addressLines.length > 0 ? (
            addressLines.map((line, index) => <DetailValue key={index}>{line}</DetailValue>)
          ) : (
            <EmptyValue />
          )}
        </ColumnBlock>
        {hasContact && (
          <ColumnBlock label={t("Contact Information")}>
            {address?.phone_number && <ContactLine icon={<PhoneOutlinedIcon />}>{address.phone_number}</ContactLine>}
            {address?.email && <ContactLine icon={<EmailOutlinedIcon />}>{address.email}</ContactLine>}
            {address?.email_counter_reminders && (
              <Tooltip title={t("Email for counter reminders")} placement="top-start">
                <Box sx={{ width: "fit-content", maxWidth: "100%" }}>
                  <ContactLine icon={<SpeedOutlinedIcon />}>{address.email_counter_reminders}</ContactLine>
                </Box>
              </Tooltip>
            )}
          </ColumnBlock>
        )}
        {withDetails && address?.notes && (
          <ColumnBlock label={t("Notes")}>
            <DetailValue multiline>{address.notes}</DetailValue>
          </ColumnBlock>
        )}
      </Box>
      {withDetails && (
        <ColumnBlock label={t("Opening Hours")}>
          <OpeningHoursValue openingHours={address?.opening_hours} />
        </ColumnBlock>
      )}
    </Box>
  );
};

export const AddressDisplay = ({
  address,
  title,
  icon,
  withDetails = false,
  withContact = false,
  collapsible = false,
  defaultExpanded = true,
  emptyContent,
}: AddressDisplayProps) => {
  const { t } = useTranslation();
  const googleMapsUrl = address ? buildGoogleMapsUrl(address) : "";
  const sectionTitle = title ?? `${t("Address")} & ${t("Opening Hours")}`;

  if (!address && emptyContent) {
    return (
      <DetailSection title={sectionTitle} icon={icon} padded>
        {emptyContent}
      </DetailSection>
    );
  }

  return (
    <DetailSection
      title={sectionTitle}
      icon={icon}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
      action={
        googleMapsUrl && (
          <Button
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            {t("View on Google Maps")}
          </Button>
        )
      }
    >
      <AddressColumns address={address} withDetails={withDetails} withContact={withContact} />
    </DetailSection>
  );
};
